import { ApiService } from "./apiService";
import { failure } from "./serviceHelpers";
import {
  Recipe,
  RecipeResponse,
  RecipePaginatedResponse,
  RecipeDetailResponse,
  CreateRecipeInput,
  UpdateRecipeInput,
  RecipeFilters,
  RecipeServiceResponse,
} from "../types/recipe";
import { logger } from "../utils/logger";

export class RecipeService {
  // Get paginated recipes list
  static async getRecipes(
    page: number = 1,
    filters: RecipeFilters = {}
  ): Promise<RecipeResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      if (filters.search) {
        params.append("search", filters.search);
      }

      const response = await ApiService.get(`/recipes?${params.toString()}`);
      const result: RecipePaginatedResponse = await ApiService.handleResponse(
        response
      );

      return result.data;
    } catch (error) {
      logger.error("Error fetching recipes:", error);
      return null;
    }
  }

  // Get single recipe by ID
  static async getRecipeById(id: number): Promise<Recipe | null> {
    try {
      const response = await ApiService.get(`/recipes/${id}`);
      const result: RecipeDetailResponse = await ApiService.handleResponse(
        response
      );

      return result.data;
    } catch (error) {
      logger.error("Error fetching recipe:", error);
      return null;
    }
  }

  static async createAdminRecipe(
    recipeData: CreateRecipeInput
  ): Promise<RecipeServiceResponse<Recipe>> {
    try {
      const response = await ApiService.post(
        "/recipes/store-admin-recipe",
        recipeData
      );
      const result: RecipeDetailResponse = await ApiService.handleResponse(
        response
      );

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to create recipe");
    }
  }

  // Update existing recipe
  static async updateRecipe(
    id: number,
    updates: UpdateRecipeInput
  ): Promise<RecipeServiceResponse<Recipe>> {
    try {
      const response = await ApiService.patch(`/recipes/${id}`, updates);
      const result: RecipeDetailResponse = await ApiService.handleResponse(
        response
      );

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to update recipe");
    }
  }

  // Delete recipe
  static async deleteRecipe(id: number): Promise<RecipeServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/recipes/${id}`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete recipe");
    }
  }

  // Search recipes
  static async searchRecipes(
    searchTerm: string,
    page: number = 1
  ): Promise<RecipeServiceResponse<RecipeResponse>> {
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm,
        page: page.toString(),
      });

      const response = await ApiService.get(
        `/recipes/search?${params.toString()}`
      );
      const result: RecipePaginatedResponse = await ApiService.handleResponse(
        response
      );

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to search recipes");
    }
  }

  // Upload recipe image
  static async uploadRecipeImage(
    recipeId: number,
    imageFile: File
  ): Promise<RecipeServiceResponse<Recipe>> {
    try {
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("recipe_id", recipeId.toString());

      // Goes through ApiService so an expired token is refreshed and the
      // upload retried; a raw fetch here used to make a 401 fatal.
      const result = await ApiService.postJson<RecipeDetailResponse>(
        `/files/recipes/${recipeId}/upload-recipe-image`,
        formData
      );

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to upload recipe image");
    }
  }

  // Delete recipe image
  static async deleteRecipeImage(
    recipeId: number,
    fileId: number
  ): Promise<RecipeServiceResponse<void>> {
    try {
      const response = await ApiService.delete(`/files/delete/${fileId}`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete recipe image");
    }
  }
}
